import { FC, useContext, useEffect, useState } from 'react';

import { useMutation, useQuery } from '@apollo/client';
import {
  Button,
  Input,
  Modal,
  NumberInput,
  Select,
  ISelectOption,
} from '@smartshell/ui-kit';
import { Controller, useForm } from 'react-hook-form';

import { ALL_LETTERS_AND_DIGITS_REGEXP } from 'consts';
import { LocalizationContext } from 'localization';
import notification from 'services/notificationService';
import { EHostType } from 'types';

import {
  IFormValues,
  IManageHostModalProps,
  IServerHostGroupTitles,
} from './interfaces';
import s from './ManageHostModal.module.scss';
import {
  CREATE_HOST,
  HOST,
  HOST_TITLES,
  ICreateHostResponse,
  IHostResponse,
  IUpdateHostResponse,
  UPDATE_HOST,
} from './schema';

export const ManageHostModal: FC<IManageHostModalProps> = ({
  closeModal,
  onSuccess,
  hostId,
  hostCoords,
}) => {
  const { t } = useContext(LocalizationContext);

  const { data: hostData } = useQuery<IHostResponse>(HOST, {
    fetchPolicy: 'cache-only',
    variables: { id: hostId },
    skip: !hostId,
  });
  const host = hostData?.hostOverview;

  const { control, handleSubmit, setValue, getValues } = useForm<IFormValues>({
    defaultValues: {
      host_type: host?.type || hostCoords?.hostType || null,
      alias: host?.alias ?? '',
      position: host?.position ?? '',
      group: null,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
  });

  const [options, setOptions] = useState<Array<ISelectOption>>([]);

  const { data: hostTitles } = useQuery<IServerHostGroupTitles>(HOST_TITLES, {
    fetchPolicy: 'no-cache',
    notifyOnNetworkStatusChange: true,
  });

  const [createHost, { loading: createLoading }] =
    useMutation<ICreateHostResponse>(CREATE_HOST, {
      onCompleted: ({ createHost: { alias } }) => {
        onSuccess();
        closeModal();
        notification.info({
          t,
          leftDescription: alias,
          title: t('host.created'),
          type: 'success',
          dismiss: 5,
        });
      },
      onError: (error) => {
        notification.info({
          t,
          type: 'error',
          title: t('errors.error'),
          message: error?.message,
        });
      },
    });

  const [updateHost, { loading: updateLoading }] =
    useMutation<IUpdateHostResponse>(UPDATE_HOST, {
      onCompleted: ({ updateHost: { alias } }) => {
        onSuccess();
        closeModal();
        notification.info({
          t,
          leftDescription: alias,
          title: t('host.updated'),
          type: 'success',
          dismiss: 5,
        });
      },
    });

  useEffect(() => {
    if (hostTitles) {
      const { hostGroupTitles: titles } = hostTitles;

      setOptions(
        titles.map((title) => ({
          value: title.id,
          title: `${title.title} ${
            title.priority !== 0
              ? '(' + t('priority').toLowerCase() + ' ' + title.priority + ')'
              : ''
          }`,
        }))
      );
    }
  }, [hostTitles]);

  useEffect(() => {
    const selectedOption = options.find(
      (option) => option.value === host?.group_id
    )?.value;

    if (selectedOption) {
      setValue('group', selectedOption);
    }
  }, [options, host?.group_id]);

  const OPTIONS = [
    { title: t('nav.hosts'), value: EHostType.PC },
    { title: t('host.map.toolbar.action_9'), value: EHostType.CONSOLE },
    { title: t('host.map.toolbar.action_10'), value: EHostType.VR },
    { title: t('host.map.toolbar.action_11'), value: EHostType.RACING_SIM },
    { title: t('host.map.toolbar.action_13'), value: EHostType.BOARD_GAME },
    { title: t('host.map.toolbar.action_12'), value: EHostType.BILLIARD },
  ];
  return (
    <Modal onClose={() => closeModal()}>
      <form
        className={s.root}
        onSubmit={handleSubmit((data) => {
          const { group, alias, position, host_type } = data;

          const input = {
            group_id: group,
            alias,
            type: host_type,
            ...(position !== '' ? { position } : {}),
            ...(hostCoords
              ? { coord_x: hostCoords.x, coord_y: hostCoords.y }
              : {}),
          };

          hostId
            ? updateHost({
                variables: {
                  id: hostId,
                  input,
                },
              }).catch(console.error)
            : createHost({
                variables: {
                  input,
                },
              }).catch(console.error);
        })}
      >
        <span className={s.header}>
          {t(hostId ? 'host.edit.title' : 'host.create.title')}
        </span>
        <div className={s.main}>
          <Controller
            render={({
              field: { onChange, ...fieldProps },
              fieldState: { error },
            }) => (
              <Select
                title={t('type')}
                {...fieldProps}
                options={OPTIONS}
                className={s.field}
                error={error?.message}
                onChange={onChange}
              />
            )}
            name="host_type"
            control={control}
            rules={{
              required: t('errors.required'),
            }}
          />
          <Controller
            render={({
              field: { onChange, ...fieldProps },
              fieldState: { error },
            }) => (
              <Input
                title={t('title')}
                {...fieldProps}
                className={s.field}
                error={error?.message}
                onChange={onChange}
                placeholder="PC-01"
                maxLength={12}
              />
            )}
            name="alias"
            control={control}
            defaultValue={host?.alias}
            rules={{
              required: t('errors.required'),
              minLength: {
                value: 3,
                message: t('errors.minLength', 3),
              },
              maxLength: {
                value: 12,
                message: t('errors.maxLength', 12),
              },
              validate: (value) =>
                ALL_LETTERS_AND_DIGITS_REGEXP.test(value)
                  ? undefined
                  : t('errors.notOnlySpecial'),
            }}
          />
          <Controller
            render={({ field: { ...fieldProps }, fieldState: { error } }) => (
              <NumberInput
                {...fieldProps}
                title={t('host.number')}
                className={s.field}
                error={error?.message}
                placeholder="1"
              />
            )}
            name="position"
            control={control}
            defaultValue={host?.position || ''}
            rules={{
              validate: (value) =>
                Number.isInteger(Number(value)) ||
                t('host.create.number.error'),
              min: { value: 0, message: t('errors.min', 0) },
              max: { value: 999, message: t('errors.max', 999) },
            }}
          />
          <Controller
            render={({ field: { ...fieldProps }, fieldState: { error } }) => {
              return (
                <Select
                  {...fieldProps}
                  error={error?.message}
                  className={s.field}
                  options={options}
                  title={t('group')}
                  disableClear={true}
                />
              );
            }}
            name="group"
            control={control}
            defaultValue={getValues('group')}
            rules={{ required: t('errors.required') }}
          />
        </div>
        <Button
          isLoading={createLoading || updateLoading}
          className={s.submit}
          type="submit"
          fullWidth
        >
          {hostId ? t('save') : t('create')}
        </Button>
      </form>
    </Modal>
  );
};
